import { useEffect } from 'react';

const BASE = 'https://financecalculator.cc';

export default function SEO({ title, description, canonical, schema }) {
  useEffect(() => {
    // Title
    document.title = title;

    // Description
    setMeta('name', 'description', description);

    // Canonical
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement('link'); link.rel = 'canonical'; document.head.appendChild(link); }
    link.href = canonical || BASE;

    // OG
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', canonical || BASE);

    // Twitter
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);

    // Structured data
    if (schema) {
      let sd = document.getElementById('page-schema');
      if (!sd) { sd = document.createElement('script'); sd.id = 'page-schema'; sd.type = 'application/ld+json'; document.head.appendChild(sd); }
      sd.textContent = JSON.stringify(schema);
    }

    return () => {
      // reset to home on unmount
      document.title = 'Free Financial Calculators — Rent, Mortgage, Retirement & More | FinanceCalculator.cc';
    };
  }, [title, description, canonical, schema]);

  return null;
}

function setMeta(attr, key, value) {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) { el = document.createElement('meta'); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute('content', value);
}

// ─── Page SEO configs ───────────────────────────────────────────────────────
export const PAGE_SEO = {
  home: {
    title: 'Free Financial Calculators — Rent, Mortgage, Retirement & More | FinanceCalculator.cc',
    description: 'Free financial calculators that explain what the numbers actually mean. Rent affordability, mortgage, retirement, credit card payoff, currency converter — no sign-up required.',
    canonical: `${BASE}/`,
  },
  rent: {
    title: 'Rent Affordability Calculator — Can I Afford This Apartment? | FinanceCalculator.cc',
    description: 'Find out if you can afford your apartment using the 30% rule. Calculate your rent-to-income ratio, debt-to-income ratio and recommended maximum rent instantly.',
    canonical: `${BASE}/rent`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      'name': 'Rent Affordability Calculator',
      'url': `${BASE}/rent`,
      'description': 'Calculate whether you can afford an apartment using the 30% rule and debt-to-income ratio.',
      'applicationCategory': 'FinanceApplication',
      'operatingSystem': 'Any',
      'offers': { '@type': 'Offer', 'price': '0', 'priceCurrency': 'USD' },
    },
  },
  'credit-card': {
    title: 'Credit Card Payoff Calculator — See Exactly When You\'ll Be Debt Free | FinanceCalculator.cc',
    description: 'Calculate exactly how long it will take to pay off your credit card debt and how much interest you\'ll pay. Find out how much you\'ll save by paying more each month.',
    canonical: `${BASE}/credit-card`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      'name': 'Credit Card Payoff Calculator',
      'url': `${BASE}/credit-card`,
      'description': 'Calculate your credit card payoff timeline and total interest paid.',
      'applicationCategory': 'FinanceApplication',
      'operatingSystem': 'Any',
      'offers': { '@type': 'Offer', 'price': '0', 'priceCurrency': 'USD' },
    },
  },
  mortgage: {
    title: 'Mortgage Affordability Calculator — How Much House Can I Afford? | FinanceCalculator.cc',
    description: 'Calculate how much house you can realistically afford based on your income, debts and down payment. Uses the trusted 28/36 rule — not just what a bank will approve.',
    canonical: `${BASE}/mortgage`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      'name': 'Mortgage Affordability Calculator',
      'url': `${BASE}/mortgage`,
      'description': 'Find out how much house you can afford using the 28/36 rule.',
      'applicationCategory': 'FinanceApplication',
      'operatingSystem': 'Any',
      'offers': { '@type': 'Offer', 'price': '0', 'priceCurrency': 'USD' },
    },
  },
  retirement: {
    title: 'Retirement Savings Calculator — Am I Saving Enough? | FinanceCalculator.cc',
    description: 'Find out if your retirement savings are on track. Calculate your projected nest egg, monthly retirement income, and how much you need to save to hit your goal.',
    canonical: `${BASE}/retirement`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      'name': 'Retirement Savings Calculator',
      'url': `${BASE}/retirement`,
      'description': 'Check if your retirement savings are on track with this free calculator.',
      'applicationCategory': 'FinanceApplication',
      'operatingSystem': 'Any',
      'offers': { '@type': 'Offer', 'price': '0', 'priceCurrency': 'USD' },
    },
  },
  'debt-invest': {
    title: 'Pay Off Debt or Invest? Calculator — Get a Clear Answer | FinanceCalculator.cc',
    description: 'Should you pay off debt or invest your extra money? This calculator compares both options with your real numbers and tells you which builds more wealth.',
    canonical: `${BASE}/debt-invest`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      'name': 'Pay Off Debt or Invest Calculator',
      'url': `${BASE}/debt-invest`,
      'description': 'Calculate whether paying off debt or investing is the better financial decision.',
      'applicationCategory': 'FinanceApplication',
      'operatingSystem': 'Any',
      'offers': { '@type': 'Offer', 'price': '0', 'priceCurrency': 'USD' },
    },
  },
  raise: {
    title: 'Salary Raise Calculator — What\'s My Raise Actually Worth? | FinanceCalculator.cc',
    description: 'Calculate your real take-home pay after a salary raise. See exactly how much goes to federal tax, state tax, Social Security and Medicare — and what lands in your pocket.',
    canonical: `${BASE}/raise`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      'name': 'Salary Raise Calculator',
      'url': `${BASE}/raise`,
      'description': 'Calculate your actual take-home pay after a salary raise, after all taxes.',
      'applicationCategory': 'FinanceApplication',
      'operatingSystem': 'Any',
      'offers': { '@type': 'Offer', 'price': '0', 'priceCurrency': 'USD' },
    },
  },
  millionaire: {
    title: 'Millionaire Calculator — How Long Until I\'m a Millionaire? | FinanceCalculator.cc',
    description: 'Find out exactly how long it will take to become a millionaire based on your savings and monthly contributions. See the power of compound interest working for you.',
    canonical: `${BASE}/millionaire`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      'name': 'Millionaire Calculator',
      'url': `${BASE}/millionaire`,
      'description': 'Calculate how long until you reach your financial goal using compound interest.',
      'applicationCategory': 'FinanceApplication',
      'operatingSystem': 'Any',
      'offers': { '@type': 'Offer', 'price': '0', 'priceCurrency': 'USD' },
    },
  },
  currency: {
    title: 'Currency Converter — Live Exchange Rates for 24 Currencies | FinanceCalculator.cc',
    description: 'Free real-time currency converter with live exchange rates from the European Central Bank. Convert between 24 major currencies instantly with daily updated rates.',
    canonical: `${BASE}/currency`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      'name': 'Currency Converter',
      'url': `${BASE}/currency`,
      'description': 'Real-time currency converter with live ECB exchange rates for 24 currencies.',
      'applicationCategory': 'FinanceApplication',
      'operatingSystem': 'Any',
      'offers': { '@type': 'Offer', 'price': '0', 'priceCurrency': 'USD' },
    },
  },
  'savings-growth': {
    title: 'Savings Growth Calculator — See How Much Your Savings Have Grown | FinanceCalculator.cc',
    description: 'Calculate exactly how much your savings have grown over any time period. Enter a start date, end date, monthly contribution and interest rate to see the power of compound interest.',
    canonical: `${BASE}/savings-growth`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      'name': 'Savings Growth Calculator',
      'url': `${BASE}/savings-growth`,
      'description': 'See how much your savings have grown over time with compound interest.',
      'applicationCategory': 'FinanceApplication',
      'operatingSystem': 'Any',
      'offers': { '@type': 'Offer', 'price': '0', 'priceCurrency': 'USD' },
    },
  },
};
